import React, { useEffect, useCallback, useRef } from 'react';
import numeral from 'numeral';
import '../styles/footnote-modal.css';
import {
  FOOTNOTE_MODAL_OPEN_EVENT,
  FOOTNOTE_MODAL_CLOSE_EVENT,
  getFootnoteModalContext,
  closeFootnoteModal,
  type FootnoteFieldInfo,
  type FootnoteCompanyInfo,
  type FootnoteOnUpdateField
} from '../utils/footnoteModalBus';
import { getPdfViewerOpen } from '../utils/pdfViewerBus';
import { devLog } from '../utils/devLogger';

export const FootnoteModal: React.FC = () => {
  const modalRootRef = useRef<HTMLDivElement>(null);
  const modalBackdropRef = useRef<HTMLDivElement>(null);
  const modalPanelRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  
  // Mounted guard - CRITICAL to prevent operations after unmount
  const mountedRef = useRef(true);
  
  const applyPdfDrawerLayout = useCallback(() => {
    // Guard against operations after unmount
    if (!mountedRef.current) return;
    if (!modalRootRef.current || !modalBackdropRef.current || !modalPanelRef.current) return;
    if (!modalRootRef.current || !modalBackdropRef.current || !modalPanelRef.current) return;
    if (modalRootRef.current.style.display !== 'flex') return;
    if (!getPdfViewerOpen()) return;

    const wrap = document.querySelector('.main-content-wrap.with-pdf-viewer') as HTMLElement | null;
    const rect = wrap?.getBoundingClientRect();
    const wrapLeft = rect ? rect.left : 0;
    const wrapWidth = rect ? rect.width : window.innerWidth;
    const leftPaneWidth = Math.round(wrapWidth * 0.6);
    const leftPaneRight = wrapLeft + leftPaneWidth;
    const rightGap = Math.max(0, Math.round(window.innerWidth - leftPaneRight));

    // Root: don't block the PDF area (right side)
    modalRootRef.current.style.pointerEvents = 'none';
    modalRootRef.current.style.alignItems = 'flex-start';
    modalRootRef.current.style.justifyContent = 'flex-start';
    modalRootRef.current.style.padding = '0';

    // Backdrop: cover sidebar + left pane
    modalBackdropRef.current.style.pointerEvents = 'auto';
    modalBackdropRef.current.style.left = '0px';
    modalBackdropRef.current.style.right = `${rightGap}px`;
    modalBackdropRef.current.style.top = '0';
    modalBackdropRef.current.style.bottom = '0';
    modalBackdropRef.current.style.background = 'rgba(0,0,0,0.15)';
    modalBackdropRef.current.style.backdropFilter = 'none';

    // Panel: dock to actual content area (after sidebar)
    modalPanelRef.current.style.pointerEvents = 'auto';
    modalPanelRef.current.style.position = 'fixed';
    modalPanelRef.current.style.left = `${Math.round(wrapLeft) + 16}px`;
    modalPanelRef.current.style.top = '16px';
    modalPanelRef.current.style.margin = '0';
    modalPanelRef.current.style.width = `${Math.max(320, leftPaneWidth - 32)}px`;
    modalPanelRef.current.style.maxWidth = 'none';
    modalPanelRef.current.style.height = 'calc(100vh - 32px)';
  }, []);

  const scheduleDrawerLayout = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      applyPdfDrawerLayout();
    });
  }, [applyPdfDrawerLayout]);

  const breakdownContainerRef = useRef<HTMLDivElement>(null);
  const currentFootnoteRef = useRef<HTMLTextAreaElement>(null);
  const comparativeFootnoteRef = useRef<HTMLTextAreaElement>(null);
  const currentTotalRef = useRef<HTMLSpanElement>(null);
  const comparativeTotalRef = useRef<HTMLSpanElement>(null);
  const comparativeTotalContainerRef = useRef<HTMLDivElement>(null);
  const fieldLabelRef = useRef<HTMLParagraphElement>(null);
  const badgeReadonlyRef = useRef<HTMLSpanElement>(null);
  const badgeEditableRef = useRef<HTMLSpanElement>(null);
  const headerGridRef = useRef<HTMLDivElement>(null);
  const totalGridRef = useRef<HTMLDivElement>(null);
  const currentHeaderLabelRef = useRef<HTMLDivElement>(null);
  const comparativeHeaderLabelRef = useRef<HTMLDivElement>(null);
  const currentFootnoteLabelRef = useRef<HTMLLabelElement>(null);
  const comparativeFootnoteLabelRef = useRef<HTMLLabelElement>(null);
  const comparativeFootnoteBlockRef = useRef<HTMLDivElement>(null);

  // Mutable context (NO STATE)
  const fieldInfoRef = useRef<FootnoteFieldInfo | null>(null);
  const companyInfoRef = useRef<FootnoteCompanyInfo | null>(null);
  const onUpdateFieldRef = useRef<FootnoteOnUpdateField | undefined>(undefined);
  const onMarkDirtyRef = useRef<(() => void) | undefined>(undefined);
  const isSingleYearRef = useRef<boolean>(false);

  // Calculate totals - PURE LOCAL CALCULATION, no external side effects
  const calculateTotalsNow = useCallback(() => {
    if (!mountedRef.current) return;
    if (!breakdownContainerRef.current) return;

    const rows = breakdownContainerRef.current.querySelectorAll('.breakdown-row');
    let currentTotal = 0;
    let comparativeTotal = 0;

    rows.forEach(row => {
      const currentInput = row.querySelector('.breakdown-current-input') as HTMLInputElement;
      const comparativeInput = row.querySelector('.breakdown-comparative-input') as HTMLInputElement;

      if (currentInput?.value) {
        const val = numeral(currentInput.value).value();
        if (val !== null) currentTotal += val;
      }
      if (comparativeInput?.value) {
        const val = numeral(comparativeInput.value).value();
        if (val !== null) comparativeTotal += val;
      }
    });

    // ONLY update the total DISPLAY inside dialog - nothing else
    if (currentTotalRef.current) {
      currentTotalRef.current.textContent = numeral(currentTotal).format('0,0.00');
    }
    if (comparativeTotalRef.current) {
      comparativeTotalRef.current.textContent = numeral(comparativeTotal).format('0,0.00');
    }
  }, []);


  const applyHeaderLayout = useCallback(() => {
    if (!mountedRef.current) return;
    
    const isSingleYear = isSingleYearRef.current;

    if (headerGridRef.current) {
      headerGridRef.current.classList.remove(
        'grid-cols-[minmax(240px,1fr)_140px_36px]',
        'grid-cols-[minmax(240px,1fr)_140px_140px_36px]'
      );
      headerGridRef.current.classList.add(
        isSingleYear ? 'grid-cols-[minmax(240px,1fr)_140px_36px]' : 'grid-cols-[minmax(240px,1fr)_140px_140px_36px]'
      );
    }

    if (totalGridRef.current) {
      totalGridRef.current.classList.remove(
        'grid-cols-[minmax(240px,1fr)_140px_36px]',
        'grid-cols-[minmax(240px,1fr)_140px_140px_36px]'
      );
      totalGridRef.current.classList.add(
        isSingleYear ? 'grid-cols-[minmax(240px,1fr)_140px_36px]' : 'grid-cols-[minmax(240px,1fr)_140px_140px_36px]'
      );
    }

    if (comparativeHeaderLabelRef.current) {
      comparativeHeaderLabelRef.current.style.display = isSingleYear ? 'none' : '';
    }

    // ✅ FIX: Hide comparative total cell in single year mode
    if (comparativeTotalContainerRef.current) {
      comparativeTotalContainerRef.current.style.display = isSingleYear ? 'none' : '';
    }

    if (comparativeFootnoteBlockRef.current) {
      comparativeFootnoteBlockRef.current.style.display = isSingleYear ? 'none' : '';
    }
  }, []);

  // Pure DOM manipulation for breakdown rows (like 1.0 - instant!)
  const createBreakdownRow = useCallback((label = '', currentValue = 0, comparativeValue = 0) => {
    if (!mountedRef.current) return null;
    
    const isSingleYear = isSingleYearRef.current;
    const row = document.createElement('div');
    // Keep numeric columns consistent (so Total can align perfectly)
    row.className = `breakdown-row grid items-center gap-2 ${isSingleYear ? 'grid-cols-[minmax(240px,1fr)_140px_36px]' : 'grid-cols-[minmax(240px,1fr)_140px_140px_36px]'}`;
    
    // Format values
    const formattedCurrent = currentValue !== 0 ? numeral(currentValue).format('0,0.00') : '';
    const formattedComparative = comparativeValue !== 0 ? numeral(comparativeValue).format('0,0.00') : '';
    
    row.innerHTML = `
      <input
        type="text"
        class="breakdown-label h-[26px] w-full rounded border border-slate-300 bg-white px-2.5 text-[13px] text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
        placeholder="Description"
        value="${label}"
        data-skip-validation="true"
      />
      <input
        type="text"
        class="breakdown-current-input h-[26px] w-full rounded border border-slate-300 bg-white px-2.5 text-[13px] text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200 text-right tabular-nums"
        placeholder="0.00"
        value="${formattedCurrent}"
        data-skip-validation="true"
      />
      ${!isSingleYear ? `<input
        type="text"
        class="breakdown-comparative-input h-[26px] w-full rounded border border-slate-300 bg-white px-2.5 text-[13px] text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200 text-right tabular-nums"
        placeholder="0.00"
        value="${formattedComparative}"
        data-skip-validation="true"
      />` : ''}
      <button
        type="button"
        class="remove-breakdown-btn inline-flex h-[26px] w-[36px] items-center justify-center justify-self-end rounded bg-rose-600 text-white shadow-sm transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-300"
        aria-label="Remove breakdown item"
      >
        ✕
      </button>
    `;
    
    // Add event listeners
    const currentInput = row.querySelector('.breakdown-current-input') as HTMLInputElement;
    const comparativeInput = row.querySelector('.breakdown-comparative-input') as HTMLInputElement | null;
    const removeBtn = row.querySelector('.remove-breakdown-btn') as HTMLButtonElement;
    
    const handleInputChange = () => calculateTotalsNow();
    
    const handleFocus = (e: FocusEvent) => {
      const input = e.target as HTMLInputElement;
      if (input.value) {
        const numValue = numeral(input.value).value();
        input.value = numValue?.toString() || '';
      }
    };
    
    const handleBlur = (e: FocusEvent) => {
      const input = e.target as HTMLInputElement;
      if (input.value) {
        const numValue = numeral(input.value).value() || 0;
        input.value = numeral(numValue).format('0,0.00');
      }
      calculateTotalsNow();
    };
    
    currentInput.addEventListener('input', handleInputChange);
    currentInput.addEventListener('focus', handleFocus as any);
    currentInput.addEventListener('blur', handleBlur as any);
    
    if (comparativeInput) {
      comparativeInput.addEventListener('input', handleInputChange);
      comparativeInput.addEventListener('focus', handleFocus as any);
      comparativeInput.addEventListener('blur', handleBlur as any);
    }
    
    removeBtn.addEventListener('click', () => {
      // Guard row.remove() to prevent removeChild crashes
      if (!mountedRef.current) return;
      if (row.parentNode) {
        row.remove();
      }
      calculateTotalsNow();
    });
    
    return row;
  }, [calculateTotalsNow, mountedRef]);

  const loadExistingDataFromButtons = useCallback(() => {
    if (!mountedRef.current) return;
    
    const fieldInfo = fieldInfoRef.current;
    if (!fieldInfo || !breakdownContainerRef.current) return;

    const isSingleYear = isSingleYearRef.current;
    
    const safeParseBreakdown = (raw: string | null | undefined): any[] => {
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.warn('⚠️ Invalid footnote breakdown JSON on button attribute, ignoring.', { raw });
        return [];
      }
    };

    // Get footnote buttons to load data from
    const currentButton = document.querySelector(
      `.footnote-edit-btn[data-context-ref="${fieldInfo.currentYearContext}"][data-qname="${fieldInfo.qname}"]`
    ) as HTMLButtonElement | null;

    const comparativeButton = document.querySelector(
      `.footnote-edit-btn[data-context-ref="${fieldInfo.comparativeYearContext}"][data-qname="${fieldInfo.qname}"]`
    ) as HTMLButtonElement | null;

    // Load footnote texts
    if (currentFootnoteRef.current) {
      currentFootnoteRef.current.value = currentButton?.getAttribute('data-footnote-text') || '';
    }
    if (comparativeFootnoteRef.current) {
      comparativeFootnoteRef.current.value = isSingleYear ? '' : (comparativeButton?.getAttribute('data-footnote-text') || '');
    }

    // Load breakdown data - MERGE by label (same label = same row)
    const currentBreakdownStr = currentButton?.getAttribute('data-footnote-breakdown');
    const comparativeBreakdownStr = comparativeButton?.getAttribute('data-footnote-breakdown');

    if (currentBreakdownStr || comparativeBreakdownStr) {
      const currentBreakdown = safeParseBreakdown(currentBreakdownStr);
      const comparativeBreakdown = safeParseBreakdown(comparativeBreakdownStr);

      // Merge by label - create union of all unique labels
      const allLabels = new Set<string>();
      currentBreakdown.forEach((item: any) => item.label && allLabels.add(item.label));
      comparativeBreakdown.forEach((item: any) => item.label && allLabels.add(item.label));

      const mergedRows = Array.from(allLabels).map(label => {
        const currentItem = currentBreakdown.find((item: any) => item.label === label);
        const comparativeItem = comparativeBreakdown.find((item: any) => item.label === label);

        return {
          label,
          currentValue: currentItem?.value || 0,
          comparativeValue: comparativeItem?.value || 0
        };
      });

      // Guard innerHTML to prevent modal-close races
      const container = breakdownContainerRef.current;
      if (container && container.isConnected && mountedRef.current) {
        container.innerHTML = '';
        mergedRows.forEach(rowData => {
          const row = createBreakdownRow(rowData.label, rowData.currentValue, rowData.comparativeValue);
          if (row) container.appendChild(row);
        });
      }
      calculateTotalsNow();
    } else {
      // Guard innerHTML to prevent modal-close races
      const container = breakdownContainerRef.current;
      if (container && container.isConnected && mountedRef.current) {
        container.innerHTML = '';
        const row = createBreakdownRow();
        if (row) container.appendChild(row);
      }
      calculateTotalsNow();
    }
  }, [calculateTotalsNow, createBreakdownRow, mountedRef]);

  const addBreakdownItem = useCallback(() => {
    if (!mountedRef.current) return;
    if (!breakdownContainerRef.current) return;
    const row = createBreakdownRow();
    if (row) breakdownContainerRef.current.appendChild(row);
  }, [createBreakdownRow, mountedRef]);



  const generateFootnoteFromBreakdown = useCallback(() => {
    if (!mountedRef.current) return;
    
    const fieldInfo = fieldInfoRef.current;
    const companyInfo = companyInfoRef.current;
    if (!fieldInfo || !companyInfo || !breakdownContainerRef.current) return;

    // Calculate totals immediately before generating footnotes
    calculateTotalsNow();

    const getMonthName = (monthNum: string) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months[parseInt(monthNum) - 1] || 'Unknown';
    };

    const getContextPeriodLabel = (contextRef: string): string => {
      if (contextRef.startsWith('fromto_')) {
        const parts = contextRef.split('_');
        if (parts.length >= 3) {
          const endDate = parts[2];
          const endMonth = getMonthName(endDate.substring(4, 6));
          const endYear = endDate.substring(0, 4);

          let suffix = ': ';
          if (companyInfo.consolidated === 'Y') {
            if (contextRef.includes('ConsolidatedMember')) {
              suffix = ' Group: ';
            } else if (contextRef.includes('SeparateMember')) {
              suffix = ' Company: ';
            }
          }

          return `${endMonth} ${endYear}${suffix}`;
        }
      } else if (contextRef.startsWith('asof_')) {
        const parts = contextRef.split('_');
        if (parts.length >= 2) {
          const date = parts[1];
          const month = getMonthName(date.substring(4, 6));
          const year = date.substring(0, 4);

          let suffix = ': ';
          if (companyInfo.consolidated === 'Y') {
            if (contextRef.includes('ConsolidatedMember')) {
              suffix = ' Group: ';
            } else if (contextRef.includes('SeparateMember')) {
              suffix = ' Company: ';
            }
          }

          return `${month} ${year}${suffix}`;
        }
      }

      return fieldInfo.currentYear + ': ';
    };

    let currentPrefix = '';
    let comparativePrefix = '';

    if (fieldInfo.currentYear === fieldInfo.comparativeYear) {
      currentPrefix = getContextPeriodLabel(fieldInfo.currentYearContext);
      comparativePrefix = getContextPeriodLabel(fieldInfo.comparativeYearContext);
    } else {
      currentPrefix = fieldInfo.currentYear + ': ';
      comparativePrefix = fieldInfo.comparativeYear + ': ';

      if (companyInfo.consolidated === 'Y') {
        if (fieldInfo.memberType === 'ConsolidatedMember') {
          currentPrefix = fieldInfo.currentYear + ' Group: ';
          comparativePrefix = fieldInfo.comparativeYear + ' Group: ';
        } else if (fieldInfo.memberType === 'SeparateMember') {
          currentPrefix = fieldInfo.currentYear + ' Company: ';
          comparativePrefix = fieldInfo.comparativeYear + ' Company: ';
        }
      }
    }

    // Generate footnote items
    const currentItems: string[] = [];
    const comparativeItems: string[] = [];

    const rows = breakdownContainerRef.current.querySelectorAll('.breakdown-row');
    rows.forEach(row => {
      const labelInput = row.querySelector('.breakdown-label') as HTMLInputElement;
      const currentInput = row.querySelector('.breakdown-current-input') as HTMLInputElement;
      const comparativeInput = row.querySelector('.breakdown-comparative-input') as HTMLInputElement;

      const label = labelInput?.value.trim() || '';

      if (label) {
        if (currentInput?.value && currentInput.value !== '0.00') {
          currentItems.push(`${label} [${currentInput.value}]`);
        }
        if (comparativeInput?.value && comparativeInput.value !== '0.00') {
          comparativeItems.push(`${label} [${comparativeInput.value}]`);
        }
      }
    });

    // Build footnote texts
    const currentTotal = currentTotalRef.current?.textContent || '0.00';
    const comparativeTotal = comparativeTotalRef.current?.textContent || '0.00';

    let currentFootnote = '';
    if (currentItems.length > 0) {
      currentFootnote = `${currentPrefix}${fieldInfo.fieldLabel} [${currentTotal}] = ${currentItems.join(' + ')}`;
    }

    let comparativeFootnote = '';
    if (comparativeItems.length > 0) {
      comparativeFootnote = `${comparativePrefix}${fieldInfo.fieldLabel} [${comparativeTotal}] = ${comparativeItems.join(' + ')}`;
    }

    if (currentFootnoteRef.current) {
      currentFootnoteRef.current.value = currentFootnote;
    }
    if (comparativeFootnoteRef.current) {
      comparativeFootnoteRef.current.value = comparativeFootnote;
    }
  }, [calculateTotalsNow]);

  const handleSave = useCallback(() => {
    if (!mountedRef.current) return;
    
    const fieldInfo = fieldInfoRef.current;
    const companyInfo = companyInfoRef.current;
    const onUpdateField = onUpdateFieldRef.current;
    const onMarkDirty = onMarkDirtyRef.current;
    if (!fieldInfo || !companyInfo || !breakdownContainerRef.current) return;
    
    // Mark parent form as dirty
    if (onMarkDirty) {
      onMarkDirty();
    }

    const isSingleYear = isSingleYearRef.current;

    // Calculate totals immediately before saving
    calculateTotalsNow();

    // Collect breakdown data
    const currentBreakdown: Array<{ label: string; value: number }> = [];
    const comparativeBreakdown: Array<{ label: string; value: number }> = [];

    const rows = breakdownContainerRef.current.querySelectorAll('.breakdown-row');
    rows.forEach(row => {
      const labelInput = row.querySelector('.breakdown-label') as HTMLInputElement;
      const currentInput = row.querySelector('.breakdown-current-input') as HTMLInputElement;

      const label = labelInput?.value.trim() || '';
      
      // Save row if it has a label OR a non-zero value
      const currentValue = numeral(currentInput?.value).value() || 0;
      
      // Current: Save if has label OR non-zero value
      if (label || currentValue !== 0) {
        currentBreakdown.push({ label, value: currentValue });
      }
      
      // Only collect comparative data if NOT single year mode
      if (!isSingleYear) {
        const comparativeInput = row.querySelector('.breakdown-comparative-input') as HTMLInputElement;
        const comparativeValue = numeral(comparativeInput?.value).value() || 0;
        
        // Comparative: Save if has label OR non-zero value
        if (label || comparativeValue !== 0) {
          comparativeBreakdown.push({ label, value: comparativeValue });
        }
      }
    });

    // Get totals
    const currentTotal = numeral(currentTotalRef.current?.textContent || '0').value() || 0;
    const comparativeTotal = numeral(comparativeTotalRef.current?.textContent || '0').value() || 0;

    // Get footnote texts
    const currentFootnoteText = currentFootnoteRef.current?.value || '';
    const comparativeFootnoteText = comparativeFootnoteRef.current?.value || '';

    // Save to button data attributes - UPDATE ALL BUTTONS with same contextRef + qname
    // This handles cases where same field appears multiple times (e.g., PPE in SOFP and SOFP-Details)
    const currentButtons = document.querySelectorAll(
      `.footnote-edit-btn[data-context-ref="${fieldInfo.currentYearContext}"][data-qname="${fieldInfo.qname}"]`
    ) as NodeListOf<HTMLButtonElement>;

    const comparativeButtons = document.querySelectorAll(
      `.footnote-edit-btn[data-context-ref="${fieldInfo.comparativeYearContext}"][data-qname="${fieldInfo.qname}"]`
    ) as NodeListOf<HTMLButtonElement>;

    devLog('💾 Saving breakdown:', {
      currentBreakdown,
      comparativeBreakdown: isSingleYear ? '(skipped - single year)' : comparativeBreakdown,
      currentButtons: currentButtons.length,
      comparativeButtons: comparativeButtons.length
    });

    // Update ALL current year buttons
    currentButtons.forEach(button => {
      button.setAttribute('data-footnote-text', currentFootnoteText);
      button.setAttribute('data-footnote-breakdown', JSON.stringify(currentBreakdown));
      button.title = currentFootnoteText || 'Click to add footnote';
      
      devLog(`💾 Saved to button: ${currentBreakdown.length} items`);

      // Update button highlighting
      if (currentFootnoteText.trim()) {
        button.classList.add('has-footnote');
      } else {
        button.classList.remove('has-footnote');
      }
    });

    // Update ALL current year anchors (so refresh can rebuild buttons correctly)
    const currentAnchors = document.querySelectorAll(
      `.footnote-anchor[data-context-ref="${fieldInfo.currentYearContext}"][data-qname="${fieldInfo.qname}"]`
    ) as NodeListOf<HTMLElement>;
    currentAnchors.forEach(anchor => {
      anchor.setAttribute('data-footnote-text', currentFootnoteText);
      anchor.setAttribute('data-footnote-breakdown', JSON.stringify(currentBreakdown));
    });

    // Update ALL comparative year buttons
    if (!isSingleYear) {
      comparativeButtons.forEach(button => {
        button.setAttribute('data-footnote-text', comparativeFootnoteText);
        button.setAttribute('data-footnote-breakdown', JSON.stringify(comparativeBreakdown));
        button.title = comparativeFootnoteText || 'Click to add footnote';

        // Update button highlighting
        if (comparativeFootnoteText.trim()) {
          button.classList.add('has-footnote');
        } else {
          button.classList.remove('has-footnote');
        }
      });

      // Update ALL comparative anchors
      const comparativeAnchors = document.querySelectorAll(
        `.footnote-anchor[data-context-ref="${fieldInfo.comparativeYearContext}"][data-qname="${fieldInfo.qname}"]`
      ) as NodeListOf<HTMLElement>;
      comparativeAnchors.forEach(anchor => {
        anchor.setAttribute('data-footnote-text', comparativeFootnoteText);
        anchor.setAttribute('data-footnote-breakdown', JSON.stringify(comparativeBreakdown));
      });
    }

    const formattedCurrentValue = numeral(currentTotal).format('0,0.00');
    const formattedComparativeValue = numeral(comparativeTotal).format('0,0.00');

    // Update main input fields with totals (respect readonly). Also fire a custom event for EditFilings.
    if (onUpdateField) {
      // Find ALL inputs with current year context and qname
      const currentInputs = document.querySelectorAll(
        `input[data-context-ref="${fieldInfo.currentYearContext}"][data-qname="${fieldInfo.qname}"]`
      ) as NodeListOf<HTMLInputElement>;

      // Update ALL current year inputs
      currentInputs.forEach(input => {
        const inputFieldName = input.getAttribute('name') || fieldInfo.fieldName;
        if (input.readOnly) return; // respect readonly
        onUpdateField(inputFieldName, fieldInfo.currentYearContext, formattedCurrentValue, fieldInfo.qname);
      });

      // Update ALL comparative year inputs (if not single year)
      if (!isSingleYear) {
        const comparativeInputs = document.querySelectorAll(
          `input[data-context-ref="${fieldInfo.comparativeYearContext}"][data-qname="${fieldInfo.qname}"]`
        ) as NodeListOf<HTMLInputElement>;

        comparativeInputs.forEach(input => {
          if (input.readOnly) return; // respect readonly
          const inputFieldName = input.getAttribute('name') || fieldInfo.fieldName;
          onUpdateField(inputFieldName, fieldInfo.comparativeYearContext, formattedComparativeValue, fieldInfo.qname);
        });
      }
    }

    // NOTE: footnoteUpdated event removed - it was triggering a FULL document traversal
    // of all .footnote-anchor elements via refreshFootnoteAnchors(), which is redundant
    // because we already update specific buttons/anchors directly above.

    // Dispatch value updates so EditFilings can force ref/HyperFormula sync (respect readonly)
    try {
      if (!fieldInfo.isReadonly) {
        document.dispatchEvent(new CustomEvent('footnoteValueUpdated', {
          detail: {
            fieldName: fieldInfo.fieldName,
            qname: fieldInfo.qname,
            contextRef: fieldInfo.currentYearContext,
            value: formattedCurrentValue
          }
        }));
        if (!isSingleYear) {
          document.dispatchEvent(new CustomEvent('footnoteValueUpdated', {
            detail: {
              fieldName: fieldInfo.fieldName,
              qname: fieldInfo.qname,
              contextRef: fieldInfo.comparativeYearContext,
              value: formattedComparativeValue
            }
          }));
        }
      }
    } catch (e) {
      // ignore
    }

    // Trigger validation AFTER all value updates and syncs are complete
    // Use setTimeout to ensure formDataRef has the new values before validation runs
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('validationNeeded'));
    }, 50);
    
    closeFootnoteModal();
  }, [calculateTotalsNow]);

  const getHeaderLabels = useCallback(() => {
    const fieldInfo = fieldInfoRef.current;
    const companyInfo = companyInfoRef.current;
    if (!fieldInfo || !companyInfo) return { current: 'Current Year', comparative: 'Comparative Year' };

    const getMonthName = (monthNum: string) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months[parseInt(monthNum) - 1] || 'Unknown';
    };

    const currentYear = fieldInfo.currentYear;
    const comparativeYear = fieldInfo.comparativeYear;
    const memberType = fieldInfo.memberType;
    const currentContext = fieldInfo.currentYearContext;
    const comparativeContext = fieldInfo.comparativeYearContext;

    // If same year AND different contexts (dual period scenario), use period-specific labels
    // But if same context (hypercube/single mode), use simple year labels
    if (currentYear === comparativeYear && currentContext !== comparativeContext) {
      const getContextLabel = (contextRef: string): string => {
        if (contextRef.startsWith('fromto_')) {
          const parts = contextRef.split('_');
          if (parts.length >= 3) {
            const endDate = parts[2];
            const endMonth = getMonthName(endDate.substring(4, 6));
            const endYear = endDate.substring(0, 4);

            let suffix = '';
            if (companyInfo.consolidated === 'Y') {
              if (contextRef.includes('ConsolidatedMember')) {
                suffix = ' Group';
              } else if (contextRef.includes('SeparateMember')) {
                suffix = ' Company';
              }
            }

            return `${endMonth} ${endYear}${suffix}`;
          }
        } else if (contextRef.startsWith('asof_')) {
          const parts = contextRef.split('_');
          if (parts.length >= 2) {
            const date = parts[1];
            const month = getMonthName(date.substring(4, 6));
            const year = date.substring(0, 4);

            let suffix = '';
            if (companyInfo.consolidated === 'Y') {
              if (contextRef.includes('ConsolidatedMember')) {
                suffix = ' Group';
              } else if (contextRef.includes('SeparateMember')) {
                suffix = ' Company';
              }
            }

            return `${month} ${year}${suffix}`;
          }
        }
        return currentYear;
      };

      return {
        current: getContextLabel(fieldInfo.currentYearContext),
        comparative: getContextLabel(fieldInfo.comparativeYearContext)
      };
    }

    // Different years - use year labels with Group/Company suffix
    let currentLabel = currentYear;
    let comparativeLabel = comparativeYear;

    if (companyInfo.consolidated === 'Y') {
      if (memberType === 'ConsolidatedMember') {
        currentLabel = `Group ${currentYear}`;
        comparativeLabel = `Group ${comparativeYear}`;
      } else if (memberType === 'SeparateMember') {
        currentLabel = `Company ${currentYear}`;
        comparativeLabel = `Company ${comparativeYear}`;
      }
    }

    return {
      current: currentLabel,
      comparative: comparativeLabel
    };
  }, []);

  const applyHeaderText = useCallback(() => {
    if (!mountedRef.current) return;
    
    const labels = getHeaderLabels();
    if (currentHeaderLabelRef.current) currentHeaderLabelRef.current.textContent = labels.current;
    if (comparativeHeaderLabelRef.current) comparativeHeaderLabelRef.current.textContent = labels.comparative;
    if (currentFootnoteLabelRef.current) currentFootnoteLabelRef.current.textContent = `${labels.current} footnote`;
    if (comparativeFootnoteLabelRef.current) comparativeFootnoteLabelRef.current.textContent = `${labels.comparative} footnote`;
  }, [getHeaderLabels, mountedRef]);

  const open = useCallback(() => {
    if (!mountedRef.current) return;
    
    const ctx = getFootnoteModalContext();
    if (!ctx) return;

    fieldInfoRef.current = ctx.fieldInfo;
    companyInfoRef.current = ctx.companyInfo;
    onUpdateFieldRef.current = ctx.onUpdateField;
    onMarkDirtyRef.current = ctx.onMarkDirty;

    // Check if single year mode:
    // 1. companyInfo.first_year === 'Y' (first year company)
    // 2. currentYearContext === comparativeYearContext (hypercube/single context mode)
    isSingleYearRef.current =
      ctx.companyInfo.first_year === 'Y' ||
      ctx.fieldInfo.currentYearContext === ctx.fieldInfo.comparativeYearContext;

    if (fieldLabelRef.current) {
      fieldLabelRef.current.textContent = ctx.fieldInfo.fieldLabel;
    }

    if (badgeReadonlyRef.current) {
      badgeReadonlyRef.current.style.display = ctx.fieldInfo.isReadonly ? '' : 'none';
    }
    if (badgeEditableRef.current) {
      badgeEditableRef.current.style.display = ctx.fieldInfo.isReadonly ? 'none' : '';
    }

    applyHeaderLayout();
    applyHeaderText();
    loadExistingDataFromButtons();

    if (modalRootRef.current) {
      modalRootRef.current.style.display = 'flex';
    }

    const pdfOpen = getPdfViewerOpen();
    if (modalRootRef.current && modalBackdropRef.current && modalPanelRef.current) {
      if (pdfOpen) {
        // Drawer mode anchored to the ACTUAL split area (after sidebar collapse)
        // Also reflows on zoom/resize via listeners below
        scheduleDrawerLayout();
      } else {
        // Centered modal mode (blocks background like before)
        modalRootRef.current.style.pointerEvents = 'auto';
        modalRootRef.current.style.alignItems = '';
        modalRootRef.current.style.justifyContent = '';
        modalRootRef.current.style.padding = '';

        modalBackdropRef.current.style.pointerEvents = '';
        modalBackdropRef.current.style.left = '0';
        modalBackdropRef.current.style.right = '0';
        modalBackdropRef.current.style.top = '0';
        modalBackdropRef.current.style.bottom = '0';
        modalBackdropRef.current.style.background = '';
        modalBackdropRef.current.style.backdropFilter = '';

        modalPanelRef.current.style.pointerEvents = '';
        modalPanelRef.current.style.position = '';
        modalPanelRef.current.style.left = '';
        modalPanelRef.current.style.top = '';
        modalPanelRef.current.style.width = '';
        modalPanelRef.current.style.maxWidth = '';
        modalPanelRef.current.style.height = '';
        modalPanelRef.current.style.margin = '';

        document.body.style.overflow = 'hidden';
      }
    } else {
      // Fallback: preserve old behavior
      document.body.style.overflow = pdfOpen ? '' : 'hidden';
    }
  }, [applyHeaderLayout, applyHeaderText, loadExistingDataFromButtons]);

  // Keep drawer proportional on zoom/resize while open in PDF mode
  useEffect(() => {
    const onResize = () => {
      if (!mountedRef.current) return;
      scheduleDrawerLayout();
    };
    
    window.addEventListener('resize', onResize);
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    vv?.addEventListener('resize', onResize);
    vv?.addEventListener('scroll', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      vv?.removeEventListener('resize', onResize);
      vv?.removeEventListener('scroll', onResize);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [scheduleDrawerLayout, mountedRef]);

  useEffect(() => {
    const onOpen = () => open();
    const onClose = () => {
      if (!mountedRef.current) return;
      if (modalRootRef.current) modalRootRef.current.style.display = 'none';
      document.body.style.overflow = '';
    };

    document.addEventListener(FOOTNOTE_MODAL_OPEN_EVENT, onOpen);
    document.addEventListener(FOOTNOTE_MODAL_CLOSE_EVENT, onClose);

    return () => {
      // Mark as unmounted
      mountedRef.current = false;
      
      document.removeEventListener(FOOTNOTE_MODAL_OPEN_EVENT, onOpen);
      document.removeEventListener(FOOTNOTE_MODAL_CLOSE_EVENT, onClose);
      
      // Cancel any pending animation frame
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      
      // Clean up breakdown container event listeners
      // Note: Row listeners are cleaned up when rows are removed (row.remove())
      // but we ensure no operations happen after unmount with mountedRef guard
      
      // Reset body overflow
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <div
      ref={modalRootRef}
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ display: 'none' }}
      onClick={(e) => e.target === e.currentTarget && closeFootnoteModal()}
    >
      <div
        ref={modalBackdropRef}
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={closeFootnoteModal}
      />

      <div
        ref={modalPanelRef}
        className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/10"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5">
          <div>
            <h3 className="text-base font-semibold text-[var(--theme-400)]">Edit Footnote</h3>
            <p ref={fieldLabelRef} className="mt-0.5 text-xs text-slate-500" />
          </div>
          <button
            type="button"
            onClick={closeFootnoteModal}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400"
            aria-label="Close"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto px-3 py-3"
          style={{ maxHeight: 'calc(90vh - 110px)' }}
        >
          {/* Single shared container (reduces repeated padding/borders) */}
          <div className="rounded-lg border border-slate-200 bg-white">
            {/* Breakdown Section */}
            <div className="border-b border-slate-200 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-slate-900">Breakdown</h4>
                <span
                  ref={badgeReadonlyRef}
                  className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900"
                  style={{ display: 'none' }}
                >
                  Update footnote only
                </span>
                <span
                  ref={badgeEditableRef}
                  className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-900"
                  style={{ display: 'none' }}
                >
                  Updates statement amount + footnote
                </span>
              </div>
            </div>

            <div className="px-3 py-2.5">
              {/* Breakdown Table Header */}
              <div
                ref={headerGridRef}
                className={`grid items-center gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-800 ${
                  'grid-cols-[minmax(240px,1fr)_140px_140px_36px]'
                }`}
              >
                <div>Description</div>
                <div ref={currentHeaderLabelRef} className="text-right">Current Year</div>
                <div ref={comparativeHeaderLabelRef} className="text-right">Comparative Year</div>
                <div />
              </div>

              {/* Breakdown Items - Pure DOM container (no React mapping) */}
              <div ref={breakdownContainerRef} className="mt-2 space-y-2">
                {/* Rows added via DOM manipulation */}
              </div>

              <div className="mt-2.5">
                <button
                  type="button"
                  onClick={addBreakdownItem}
                  className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-2.5 py-1 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-400"
                >
                  <span className="text-base leading-none">+</span>
                  Add item
                </button>

                {/* Totals (same grid as breakdown rows) */}
                <div
                  ref={totalGridRef}
                  className={`mt-2 grid items-center gap-2 rounded-md bg-slate-100 px-2.5 py-1.5 text-[13px] font-semibold text-slate-700 ${
                    'grid-cols-[minmax(240px,1fr)_140px_140px_36px]'
                  }`}
                >
                  <div className="text-right pr-2">Total</div>
                  <div className="text-right tabular-nums">
                    <span ref={currentTotalRef}>0.00</span>
                  </div>
                  <div ref={comparativeTotalContainerRef} className="text-right tabular-nums">
                    <span ref={comparativeTotalRef}>0.00</span>
                  </div>
                  <div />
                </div>
              </div>

              <button
                type="button"
                onClick={generateFootnoteFromBreakdown}
                className="mt-3 w-full rounded-md bg-sky-600 px-3 py-2 text-[13px] font-semibold text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-400"
              >
                Generate footnotes from breakdown
              </button>
            </div>

            {/* Footnote Textareas (inside same shared container) */}
            <div className="border-t border-slate-200 px-3 py-3">
              <div className="grid gap-3">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label ref={currentFootnoteLabelRef} className="text-sm font-semibold text-slate-900">
                      Current Year footnote
                    </label>
                    <button
                      type="button"
                      onClick={() => currentFootnoteRef.current && (currentFootnoteRef.current.value = '')}
                      className="text-sm font-medium text-sky-700 hover:text-sky-800 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                  <textarea
                    ref={currentFootnoteRef}
                    rows={3}
                    className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-[13px] text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
                    placeholder="Enter footnote text..."
                    data-skip-validation="true"
                  />
                </div>

                <div ref={comparativeFootnoteBlockRef}>
                  <div className="mb-2 flex items-center justify-between">
                    <label ref={comparativeFootnoteLabelRef} className="text-sm font-semibold text-slate-900">
                      Comparative Year footnote
                    </label>
                    <button
                      type="button"
                      onClick={() => comparativeFootnoteRef.current && (comparativeFootnoteRef.current.value = '')}
                      className="text-sm font-medium text-sky-700 hover:text-sky-800 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                  <textarea
                    ref={comparativeFootnoteRef}
                    rows={3}
                    className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-[13px] text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
                    placeholder="Enter footnote text..."
                    data-skip-validation="true"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
          <button
            type="button"
            onClick={closeFootnoteModal}
            className="inline-flex items-center justify-center rounded border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-400"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="inline-flex items-center justify-center rounded bg-slate-900 px-3 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400"
          >
            Save & Close
          </button>
        </div>
      </div>
    </div>
  );
};
